import type { MouseEvent, ReactNode } from 'react'
import {
  ArrowUpIcon,
  Check,
  CornerUpRight,
  ListPlus,
  Play,
  SquareIcon,
} from 'lucide-react'

import { Kbd } from '@/components/ui/kbd'
import { PromptInputSubmit } from '@/components/ui/shadcn-io/ai/prompt-input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getModifierKey } from '@/lib/platform'
import { cn } from '@/lib/utils'

import {
  alternateFollowUp,
  DEFAULT_FOLLOW_UP,
  FOLLOW_UP_VERB,
  type ComposerAction,
} from './composerDispatch'

const BUTTONS: Record<ComposerAction, { label: string; icon: ReactNode }> = {
  send: { label: 'Send message', icon: <ArrowUpIcon /> },
  stop: { label: 'Stop generating', icon: <SquareIcon className="size-4" /> },
  queue: { label: 'Queue message', icon: <ListPlus /> },
  steer: { label: 'Send now', icon: <CornerUpRight /> },
  update: { label: 'Update queued message', icon: <Check /> },
  resume: { label: 'Resume thread', icon: <Play className="fill-current" /> },
}

const ACTIONS = Object.keys(BUTTONS) as ComposerAction[]
/** Icon-swap lane: the outgoing glyph shrinks and blurs out as the next lands. */
const ICON =
  'col-start-1 row-start-1 flex transition-[opacity,filter,scale] duration-fast ease-in-out'
const ICON_HIDDEN = 'scale-25 opacity-0 blur-[2px]'

const MOD = getModifierKey()
const followUpTooltip = (
  <>
    Click to {FOLLOW_UP_VERB[DEFAULT_FOLLOW_UP]}, <Kbd>{MOD}</Kbd>-click or{' '}
    <Kbd>{MOD}</Kbd>+<Kbd>Enter</Kbd> to{' '}
    {FOLLOW_UP_VERB[alternateFollowUp(DEFAULT_FOLLOW_UP)]}
  </>
)

/** The composer's one round button; its icon and label follow the action. */
export function ComposerSubmitButton({
  action,
  disabled,
  onClick,
  withTooltip,
}: {
  action: ComposerAction
  disabled: boolean
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
  withTooltip: boolean
}) {
  const { label } = BUTTONS[action]
  // Stop and Resume act on the run, not on the typed text.
  const acts = action === 'stop' || action === 'resume'
  const button = (
    <PromptInputSubmit
      disabled={!acts && disabled}
      type={acts ? 'button' : 'submit'}
      onClick={onClick}
      aria-label={label}
      className="size-8 rounded-full bg-david-solid hover:bg-david-hover-solid text-white focus-visible:ring-2 focus-visible:ring-david-solid/40">
      {/* Every icon stays mounted in one cell so a change cross-fades. */}
      <span className="grid">
        {ACTIONS.map((a) => (
          <span key={a} className={cn(ICON, a !== action && ICON_HIDDEN)}>
            {BUTTONS[a].icon}
          </span>
        ))}
      </span>
    </PromptInputSubmit>
  )
  if (!withTooltip) return button
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>
          {action === 'queue' || action === 'steer' ? followUpTooltip : label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
